<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { route } from 'ziggy-js';
import AppContainer from '@apparatix/library/AppContainer.vue';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import { useEndpoint } from '@apparatix/composables/useEndpoint';
import { useToastNotifications } from '@apparatix/composables/useToastNotifications';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Card from 'primevue/card';
import ConfirmDialog from 'primevue/confirmdialog';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';
import Tag from 'primevue/tag';
import { useConfirm } from 'primevue/useconfirm';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import GaugeIcon from '@primeicons/vue/gauge';
import PlayIcon from '@primeicons/vue/play';
import RefreshIcon from '@primeicons/vue/refresh';
import SpinnerIcon from '@primeicons/vue/spinner';
import TimesCircleIcon from '@primeicons/vue/times-circle';

type CommandState = 'idle' | 'queued' | 'running' | 'stale';

interface CommandMeta {
  source?: string;
  state?: CommandState;
  queued_at?: string;
  started_at?: string;
  last_heartbeat?: string;
  requested_by?: string;
}

interface CommandStatus {
  tenant: string;
  is_running: boolean;
  state: CommandState;
  meta: CommandMeta | null;
  last_completed_at: string | null;
}

interface CommandResponse {
  success: boolean;
  message?: string;
  data?: CommandStatus;
}

const status = ref<CommandStatus | null>(null);
const statusError = ref('');
const actionError = ref('');
const lastCheckedAt = ref<string | null>(null);
const request = ref<'status' | 'run' | 'clear' | null>(null);
const { showSuccess, showError } = useToastNotifications();
const confirm = useConfirm();
const confirmGroup = 'adminCommand';
let active = false;
let confirmationOpen = false;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

const applyStatus = (response?: CommandResponse | null) => {
  if (active && response?.data) {
    status.value = response.data;
  }
};

const { getResults: refreshStatus, abortRequest: abortStatus } = useEndpoint<CommandResponse, CommandResponse>({
  type: 'GET',
  url: route('admin.pacing-build.status'),
  successField: 'success',
  onSuccess: response => {
    if (!active) return;
    if (!response?.data) {
      statusError.value = 'Unable to load command status.';
      return;
    }
    applyStatus(response);
    statusError.value = '';
    lastCheckedAt.value = new Date().toISOString();
  },
  onError: response => {
    if (active) statusError.value = response?.message ?? 'Unable to load command status.';
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const { getResults: queueBuild } = useEndpoint<CommandResponse, CommandResponse>({
  type: 'POST',
  url: route('admin.pacing-build.store'),
  successField: 'success',
  onSuccess: response => {
    if (!active) return;
    applyStatus(response);
    showSuccess(response?.message ?? 'The pacing build has been queued.', 'Admin Commands');
  },
  onError: response => {
    if (!active) return;
    applyStatus(response);
    actionError.value = response?.message ?? 'Unable to queue the pacing build.';
    showError(actionError.value, 'Admin Commands');
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const { getResults: clearState } = useEndpoint<CommandResponse, CommandResponse>({
  type: 'POST',
  url: route('admin.pacing-build.clear'),
  successField: 'success',
  onSuccess: response => {
    if (!active) return;
    applyStatus(response);
    showSuccess(response?.message ?? 'The command state has been cleared.', 'Admin Commands');
  },
  onError: response => {
    if (!active) return;
    applyStatus(response);
    actionError.value = response?.message ?? 'Unable to clear the command state.';
    showError(actionError.value, 'Admin Commands');
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const canAct = computed(() => !!status.value && !statusError.value && !request.value);
const canRun = computed(() => canAct.value && status.value?.state === 'idle');
const canClear = computed(() => canAct.value && status.value?.state === 'stale');
const stateSeverity = computed(() => {
  if (!status.value || statusError.value) return 'secondary';
  return ({
    idle: 'success', queued: 'info', running: 'warn', stale: 'danger',
  } as const)[status.value.state];
});
const stateLabel = computed(() => {
  if (!status.value) return statusError.value ? 'Unavailable' : 'Loading';
  const label = status.value.state.charAt(0).toUpperCase() + status.value.state.slice(1);
  return statusError.value ? `${label} (last known)` : label;
});
const stateDescription = computed(() => {
  if (statusError.value) return 'Refresh status successfully before running or clearing a command.';
  if (!status.value) return 'Checking whether a pacing build is already queued or running…';
  return {
    idle: 'Ready to run. No pacing build is queued or running for this tenant.',
    queued: 'The pacing build is queued. You can leave this page while it waits to start.',
    running: 'The pacing build is running. You can leave this page and check back later.',
    stale: 'The build has stopped reporting activity. Review the recovery instructions below.',
  }[status.value.state];
});

const formatDate = (value?: string | null) => {
  if (!value) return 'None recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const activityDetails = computed(() => [
  { label: 'Tenant', value: status.value?.tenant },
  { label: 'Last completed', value: formatDate(status.value?.last_completed_at) },
  { label: 'Queued', value: formatDate(status.value?.meta?.queued_at) },
  { label: 'Started', value: formatDate(status.value?.meta?.started_at) },
  { label: 'Requested by', value: status.value?.meta?.requested_by ?? 'None recorded' },
  { label: 'Last heartbeat', value: formatDate(status.value?.meta?.last_heartbeat) },
]);

const stopRefreshTimer = () => {
  clearTimeout(refreshTimer);
  refreshTimer = undefined;
};

const loadStatus = async () => {
  if (!active || request.value) return;
  stopRefreshTimer();
  request.value = 'status';
  try {
    await refreshStatus<CommandResponse>();
  } finally {
    request.value = null;
    if (active) refreshTimer = setTimeout(loadStatus, 10000);
  }
};

const executeCommand = async (action: 'run' | 'clear', tenant: string) => {
  confirmationOpen = false;
  if (!active) return;
  if (!(action === 'run' ? canRun.value : canClear.value) || status.value?.tenant !== tenant) {
    showError('The command status has changed or is being refreshed. Review the status and try again.', 'Admin Commands');
    return;
  }
  stopRefreshTimer();
  request.value = action;
  actionError.value = '';
  try {
    if (action === 'run') {
      await queueBuild<CommandResponse>();
    } else {
      await clearState<CommandResponse>();
    }
  } finally {
    request.value = null;
    await loadStatus();
  }
};

const confirmCommand = (action: 'run' | 'clear', event: MouseEvent) => {
  if (!(action === 'run' ? canRun.value : canClear.value) || confirmationOpen) return;
  const tenant = status.value.tenant;
  const isRun = action === 'run';
  confirm.require({
    group: confirmGroup,
    target: event.currentTarget as HTMLElement,
    header: isRun ? 'Run Pacing Build?' : 'Force Clear State?',
    message: isRun
      ? `Queue a pacing rebuild for ${tenant}? This usually takes 1–6 hours.\n\nReporting and availability numbers may look incomplete while the rebuild runs. Continue only after confirming no matching build is already queued or running.`
      : `Clear the pacing build state for ${tenant}? Only continue after confirming no build is still running.\n\nClearing the state does not stop a running build. It allows another build to be queued.`,
    accept: () => { void executeCommand(action, tenant); },
    reject: () => { confirmationOpen = false; },
    onShow: () => { confirmationOpen = true; },
    onHide: () => { confirmationOpen = false; },
    defaultFocus: 'reject',
    blockScroll: true,
    acceptLabel: isRun ? 'Run Pacing Build' : 'Force Clear State',
    rejectLabel: 'Cancel',
    acceptProps: { severity: isRun ? 'warn' : 'danger' },
    rejectProps: { severity: 'secondary', variant: 'outlined' },
  });
};

onMounted(() => {
  active = true;
  void loadStatus();
});

onUnmounted(() => {
  active = false;
  stopRefreshTimer();
  abortStatus();
  if (confirmationOpen) confirm.close();
});
</script>

<template>
  <AppContainer>
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-6 text-[var(--p-text-color)]">
      <header class="flex items-center gap-3">
        <Avatar shape="circle" class="shrink-0 bg-[var(--p-highlight-background)] text-[var(--p-highlight-color)]">
          <template #icon>
            <GaugeIcon aria-hidden="true" />
          </template>
        </Avatar>
        <div class="flex flex-col gap-1">
          <Typography variant="page-title">
            Admin Commands
          </Typography>
          <Typography variant="small-body" muted>
            Run maintenance commands for the current tenant.
          </Typography>
        </div>
      </header>

      <div class="grid items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card class="min-w-0">
          <template #title>
            <Typography variant="section-heading">
              Pacing Build
            </Typography>
          </template>
          <template #content>
            <div class="flex flex-col gap-6">
              <Typography variant="body" muted>
                Recalculate billing, revenue distribution, inventory, and pacing aggregates for this tenant.
              </Typography>
              <div class="flex flex-wrap items-center gap-2 text-sm">
                <Tag severity="secondary" value="Estimated duration: 1–6 hours" />
                <span class="text-[var(--p-text-muted-color)]">Applies to this tenant only</span>
              </div>
              <Message severity="warn" :closable="false">
                Reporting and availability numbers may look incomplete while the rebuild runs.
                Plan for a time when this will have the least impact on your team.
              </Message>
              <div class="flex flex-col gap-3 border-t border-[var(--p-content-border-color)] pt-5">
                <p class="text-sm" role="status">
                  {{ stateDescription }}
                </p>
                <Message v-if="actionError" severity="error" :closable="false">
                  {{ actionError }}
                </Message>
                <Button
                  class="w-full sm:w-auto sm:self-start"
                  :disabled="!canRun"
                  :aria-busy="request === 'run'"
                  @click="confirmCommand('run', $event)"
                >
                  <SpinnerIcon v-if="request === 'run'" spin aria-hidden="true" />
                  <PlayIcon v-else aria-hidden="true" />
                  {{ request === 'run' ? 'Queueing build…' : 'Run Pacing Build' }}
                </Button>
              </div>
              <section
                v-if="status?.state === 'stale'"
                aria-labelledby="pacing-recovery-heading"
                class="flex flex-col gap-3 border-t border-[var(--p-content-border-color)] pt-5"
              >
                <h3 id="pacing-recovery-heading" class="text-base font-semibold">
                  Recover stale state
                </h3>
                <Message severity="error" :closable="false">
                  No recent build activity was reported. The worker may have stopped unexpectedly.
                  Confirm no build is still running before clearing its state.
                </Message>
                <Typography variant="small-body" muted>
                  Clearing the state does not stop a running build. It allows another build to be queued.
                </Typography>
                <Button
                  severity="danger"
                  variant="outlined"
                  class="w-full sm:w-auto sm:self-start"
                  :disabled="!canClear"
                  :aria-busy="request === 'clear'"
                  @click="confirmCommand('clear', $event)"
                >
                  <SpinnerIcon v-if="request === 'clear'" spin aria-hidden="true" />
                  <TimesCircleIcon v-else aria-hidden="true" />
                  {{ request === 'clear' ? 'Clearing state…' : 'Force Clear State' }}
                </Button>
              </section>
            </div>
          </template>
        </Card>

        <Card class="min-w-0">
          <template #title>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <Typography variant="section-heading">
                Status &amp; activity
              </Typography>
              <Tag :severity="stateSeverity" :value="stateLabel" />
            </div>
          </template>
          <template #content>
            <div class="flex flex-col gap-5">
              <Message v-if="statusError" severity="error" :closable="false">
                {{ statusError }}
                <span v-if="status">The details below are the last known values and may be outdated.</span>
              </Message>
              <div
                v-if="!status && !statusError"
                class="flex flex-col gap-5"
                aria-label="Loading command status"
                aria-busy="true"
              >
                <Skeleton v-for="item in 6" :key="item" height="2.5rem" />
              </div>
              <dl v-else-if="status" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <div v-for="item in activityDetails" :key="item.label" class="flex min-w-0 flex-col gap-1">
                  <dt class="text-sm text-[var(--p-text-muted-color)]">
                    {{ item.label }}
                  </dt>
                  <dd class="break-words text-sm font-medium">
                    {{ item.value }}
                  </dd>
                </div>
              </dl>
              <div class="flex flex-col gap-3 border-t border-[var(--p-content-border-color)] pt-4">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <p class="text-xs text-[var(--p-text-muted-color)]">
                    Last checked: {{ lastCheckedAt ? formatDate(lastCheckedAt) : 'Not yet checked' }}
                  </p>
                  <Button
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    :disabled="!!request"
                    :aria-busy="request === 'status'"
                    aria-label="Refresh command status"
                    @click="loadStatus"
                  >
                    <SpinnerIcon v-if="request === 'status'" spin aria-hidden="true" />
                    <RefreshIcon v-else aria-hidden="true" />
                    {{ request === 'status' ? 'Refreshing…' : 'Refresh' }}
                  </Button>
                </div>
                <Typography variant="small-subtitle" muted>
                  Automatically refreshes every 10 seconds.
                </Typography>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>
    <ConfirmDialog :group="confirmGroup" :draggable="false" class="w-[calc(100vw-2rem)] max-w-lg">
      <template #message="{ message }">
        <div class="flex items-start gap-3">
          <ExclamationTriangleIcon class="mt-1 shrink-0 text-[var(--p-message-warn-color)]" aria-hidden="true" />
          <p class="whitespace-pre-line break-words text-sm leading-6">
            {{ message.message }}
          </p>
        </div>
      </template>
    </ConfirmDialog>
  </AppContainer>
</template>
